// Справочник «Страны и города»: названия на 4 языках (RU, EN, DE, UA).
// UA — украинский, используется вторым сайтом; на основном сайте не показывается.
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import {
    createCityRequest,
    createCountryRequest,
    getCitiesRequest,
    getCountriesRequest,
    updateCityRequest,
    updateCountryRequest,
} from "../../services/api/geo.api";
import { CityItem, CountryItem } from "../../services/types/geo.types";

type GeoTab = "countries" | "cities";

interface FormState {
    countryId: string;
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
}

const initialFormState: FormState = {
    countryId: "",
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
};

const LANG_FIELDS: { key: "nameRu" | "nameEn" | "nameDe" | "nameUa"; label: string; required: boolean }[] = [
    { key: "nameRu", label: "Название RU", required: true },
    { key: "nameEn", label: "Название EN", required: true },
    { key: "nameDe", label: "Название DE", required: true },
    { key: "nameUa", label: "Название UA (второй сайт)", required: false },
];

const GeoPage: React.FC = () => {
    const [tab, setTab] = useState<GeoTab>("cities");
    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [cities, setCities] = useState<CityItem[]>([]);
    const [countryFilter, setCountryFilter] = useState("");
    const [onlyWithoutUa, setOnlyWithoutUa] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const countryName = (id: string) => countries.find((c) => c.id === id)?.name_ru ?? "—";

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);
        try {
            const [countriesResponse, citiesResponse] = await Promise.all([
                getCountriesRequest(),
                getCitiesRequest(),
            ]);
            setCountries(Array.isArray(countriesResponse) ? countriesResponse : []);
            setCities(Array.isArray(citiesResponse) ? citiesResponse : []);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить страны и города");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState({ ...initialFormState, countryId: countryFilter });
        setFormError(null);
        setSuccessMessage(null);
    };

    const switchTab = (next: GeoTab) => {
        setTab(next);
        setSelectedId(null);
        setFormState({ ...initialFormState, countryId: countryFilter });
        setFormError(null);
        setSuccessMessage(null);
    };

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        const source: (CountryItem | CityItem)[] =
            tab === "countries"
                ? countries
                : cities.filter((city) => !countryFilter || city.country_id === countryFilter);
        return source
            .filter((item) => !onlyWithoutUa || !(item.name_ua ?? "").trim())
            .filter(
                (item) =>
                    !query ||
                    [item.name_ru, item.name_en, item.name_de, item.name_ua].some((value) =>
                        (value ?? "").toLowerCase().includes(query),
                    ),
            )
            .sort((a, b) => (a.name_ru ?? "").localeCompare(b.name_ru ?? "", "ru", { sensitivity: "base" }));
    }, [tab, countries, cities, countryFilter, onlyWithoutUa, searchValue]);

    const handleSelect = (item: CountryItem | CityItem) => {
        setSelectedId(item.id);
        setFormState({
            countryId: "country_id" in item ? item.country_id : "",
            nameRu: item.name_ru ?? "",
            nameEn: item.name_en ?? "",
            nameDe: item.name_de ?? "",
            nameUa: item.name_ua ?? "",
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Заполните названия RU, EN и DE (UA — по желанию).");
            return;
        }
        if (tab === "cities" && !formState.countryId) {
            setFormError("Выберите страну города.");
            return;
        }

        const names = {
            name_ru: formState.nameRu.trim(),
            name_en: formState.nameEn.trim(),
            name_de: formState.nameDe.trim(),
            name_ua: formState.nameUa.trim() || null,
        };

        setIsSaving(true);
        try {
            if (tab === "countries") {
                const saved = selectedId
                    ? await updateCountryRequest(selectedId, names)
                    : await createCountryRequest(names);
                setCountries((prev) =>
                    selectedId ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev],
                );
                setSelectedId(saved.id);
                setSuccessMessage(selectedId ? "Страна обновлена." : "Страна создана.");
            } else {
                const payload = { ...names, country_id: formState.countryId };
                const saved = selectedId
                    ? await updateCityRequest(selectedId, payload)
                    : await createCityRequest(payload);
                setCities((prev) =>
                    selectedId ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev],
                );
                setSelectedId(saved.id);
                setSuccessMessage(selectedId ? "Город обновлён." : "Город создан.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось сохранить");
        } finally {
            setIsSaving(false);
        }
    };

    const isCities = tab === "cities";
    const withoutUaCount = (isCities ? cities : countries).filter((item) => !(item.name_ua ?? "").trim()).length;

    return (
        <div className="page-content">
            <PageHeader
                title="Страны и города"
                description="Названия стран и городов на четырёх языках. UA (украинский) показывается только на втором сайте."
                actions={
                    <Button variant="primary" onClick={resetForm}>
                        {isCities ? "Новый город" : "Новая страна"}
                    </Button>
                }
            />

            <div className="geo-tabs">
                <Button variant={isCities ? "primary" : "secondary"} onClick={() => switchTab("cities")}>
                    Города ({cities.length})
                </Button>
                <Button variant={!isCities ? "primary" : "secondary"} onClick={() => switchTab("countries")}>
                    Страны ({countries.length})
                </Button>
            </div>

            <section className="content-split geo-layout">
                <Card className="content-list-card">
                    <div className="geo-toolbar">
                        <div>
                            <h3 className="card-title">{isCities ? "Список городов" : "Список стран"}</h3>
                            <p className="card-text">Без перевода UA: {withoutUaCount}</p>
                        </div>

                        <div className="geo-toolbar-controls">
                            {isCities ? (
                                <select
                                    className="ui-select"
                                    value={countryFilter}
                                    onChange={(e) => setCountryFilter(e.target.value)}
                                >
                                    <option value="">Все страны</option>
                                    {countries.map((country) => (
                                        <option key={country.id} value={country.id}>
                                            {country.name_ru}
                                        </option>
                                    ))}
                                </select>
                            ) : null}
                            <select
                                className="ui-select"
                                value={onlyWithoutUa ? "no_ua" : ""}
                                onChange={(e) => setOnlyWithoutUa(e.target.value === "no_ua")}
                            >
                                <option value="">Все</option>
                                <option value="no_ua">Без UA</option>
                            </select>
                            <Input
                                type="text"
                                placeholder="Поиск по названию..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Ничего не найдено</h3>
                            <p className="empty-block-text">Измените фильтр или создайте новую запись.</p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>RU</th>
                                    <th>EN</th>
                                    <th>DE</th>
                                    <th>UA</th>
                                    {isCities ? <th>Страна</th> : null}
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={item.id === selectedId ? "data-table-row-active" : ""}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <td>
                                            <span className="table-primary-text">{item.name_ru}</span>
                                        </td>
                                        <td>{item.name_en}</td>
                                        <td>{item.name_de}</td>
                                        <td>
                                            {item.name_ua ? (
                                                item.name_ua
                                            ) : (
                                                <span className="geo-missing">нет</span>
                                            )}
                                        </td>
                                        {isCities ? (
                                            <td>{countryName((item as CityItem).country_id)}</td>
                                        ) : null}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId
                                    ? isCities ? "Редактирование города" : "Редактирование страны"
                                    : isCities ? "Новый город" : "Новая страна"}
                            </h3>
                            <p className="card-text">RU, EN и DE обязательны, UA — для второго сайта.</p>
                        </div>
                        {selectedId ? (
                            <span className="editor-badge">ID выбран</span>
                        ) : (
                            <span className="editor-badge editor-badge-soft">Создание</span>
                        )}
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid">
                            {isCities ? (
                                <Field label="Страна" htmlFor="geo-country">
                                    <select
                                        id="geo-country"
                                        className="ui-select"
                                        value={formState.countryId}
                                        onChange={(e) =>
                                            setFormState((prev) => ({ ...prev, countryId: e.target.value }))
                                        }
                                    >
                                        <option value="">Выберите страну</option>
                                        {countries.map((country) => (
                                            <option key={country.id} value={country.id}>
                                                {country.name_ru}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            ) : null}

                            {LANG_FIELDS.map((field) => (
                                <Field key={field.key} label={field.label} htmlFor={`geo-${field.key}`}>
                                    <Input
                                        id={`geo-${field.key}`}
                                        type="text"
                                        value={formState[field.key]}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, [field.key]: event.target.value }))
                                        }
                                    />
                                </Field>
                            ))}
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохранение..." : selectedId ? "Сохранить изменения" : "Создать"}
                            </Button>
                            <Button type="button" variant="secondary" onClick={resetForm} disabled={isSaving}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default GeoPage;
